import { difference } from "lodash-es";
import { bibleStore, OfflineStore } from "../stores";
import { Progress, ProgressSubscriber } from "../progress";
import {
  getBibleUrl,
  getBooksUrl,
  getChapterUrl,
  headers
} from "../../api/BibleApi";
import { Book, Bible } from "../../api/typedefs/Bible";
import { fetchToStore } from "../fetchers";
import {
  trackBibleTextDownload,
  trackBibleTextDownloadCancel,
  trackBibleTextDownloadRemoval
} from "utils/analytics/tracking";
import pLimit from "p-limit";

const getChapterIds = (books: Book[]): string[] => {
  return books.map(book => book.chapters.map(chapter => chapter.id)).flat();
};

async function storeBible(bibleId: string, signal: AbortSignal) {
  return await fetchToStore<{ data: Bible }>(
    getBibleUrl(bibleId),
    {
      headers,
      signal
    },
    bibleStore
  ).then(body => body.data);
}

async function storeBooks(bibleId: string, signal: AbortSignal) {
  return await fetchToStore<{ data: Book[] }>(
    getBooksUrl(bibleId),
    {
      headers,
      signal
    },
    bibleStore
  ).then(body => body.data);
}

async function storeChapter(
  bibleId: string,
  chapterId: string,
  signal: AbortSignal
) {
  return await fetchToStore(
    getChapterUrl(bibleId, chapterId),
    {
      headers,
      signal
    },
    bibleStore
  );
}

export class BibleDownloader {
  store: OfflineStore;

  downloadsInProgress = new Set();
  failedDownloads = new Set();
  abortingDownloads = new Set();
  deletingDownloads = new Set();
  staleStorageKeysCache = true;
  storageKeysCache = [] as string[];

  constructor(store: OfflineStore) {
    if (!store) throw new Error("must provide store");
    this.store = store;
    // preps storage keys cache for faster retrevials later
    // and cleans downloads if needed
    this.storageKeys().then(keys => this.cleanPartials(keys));
  }

  async cleanPartials(storageKeys: string[]) {
    const bibleUrlRegex = new RegExp(`${getBibleUrl("")}[a-z0-9]{16}-\\d{2}$`);
    const bibleKeys = storageKeys.filter(key => key.match(bibleUrlRegex));
    const bibleIds = bibleKeys.map(key => key.slice(-19));
    const downloadProgresses = await Promise.all(
      bibleIds.map(bibleId => this.getDownloadProgress(bibleId))
    );
    const partialDownloads = bibleIds.reduce((acc, bibleId, index) => {
      const state = this.getDownloadState(bibleId, downloadProgresses[index]);
      if (state === "PARTIAL_DOWNLOAD") acc.push(bibleId);
      return acc;
    }, [] as string[]);

    return await Promise.all(partialDownloads.map(id => this.deleteBible(id)));
  }

  async storageKeys() {
    if (this.staleStorageKeysCache) {
      const storageKeys = await this.store.keys();
      this.storageKeysCache = storageKeys;
      this.staleStorageKeysCache = false;
    }

    return this.storageKeysCache;
  }

  downloadBible(bibleId: string, progressSubscriber: ProgressSubscriber) {
    const controller = new AbortController();

    const downloadBibleResources = async () => {
      this.downloadsInProgress.add(bibleId);
      this.failedDownloads.delete(bibleId);

      // HACK: since deletions can take over 30 seconds, we need to wait before downloading
      // but deletion step will clear any statuses for the download, so we'll need to re-add
      // download in progress.
      await this.waitForActiveDeletions(bibleId);
      this.downloadsInProgress.add(bibleId);

      const progress = new Progress(progressSubscriber);

      const initialStorageSteps: [Promise<Bible>, Promise<Book[]>] = [
        storeBible(bibleId, controller.signal),
        storeBooks(bibleId, controller.signal)
      ];

      try {
        const [, books] = await Promise.all(initialStorageSteps);

        const chapterIds = getChapterIds(books);
        progress.bump(
          initialStorageSteps.length,
          chapterIds.length + initialStorageSteps.length
        );

        const limit = pLimit(10); // limit to 10 concurrent requests at a given time
        await Promise.all(
          chapterIds.map(chapterId =>
            limit(() =>
              storeChapter(bibleId, chapterId, controller.signal).then(data => {
                progress.bump(1);
                return data;
              })
            )
          )
        );
      } catch (err) {
        this.downloadsInProgress.delete(bibleId);
        this.staleStorageKeysCache = true;

        if (err.toString().includes("AbortError")) {
          // delete downloaded texts if aborted
          await this.deleteBible(bibleId);
          this.abortingDownloads.delete(bibleId);
          trackBibleTextDownloadCancel(bibleId);
        } else {
          // abort other downloads if another error occured
          controller.abort();
          this.failedDownloads.add(bibleId);
          console.log(err);
        }

        progress.reset();
        return;
      }

      this.downloadsInProgress.delete(bibleId);
      this.staleStorageKeysCache = true;
      trackBibleTextDownload(bibleId);
    };

    downloadBibleResources();

    return () => {
      controller.abort();
      this.abortingDownloads.add(bibleId);
    };
  }

  async getBibleStorageKeys(bibleId: string) {
    const bibleIdRegex = new RegExp(bibleId);
    const storageKeys = await this.storageKeys();
    return storageKeys.filter(key => bibleIdRegex.test(key));
  }

  async deleteBible(bibleId: string) {
    this.deletingDownloads.add(bibleId);

    const bibleStorageKeys = await this.getBibleStorageKeys(bibleId);

    await Promise.all(bibleStorageKeys.map(key => this.store.removeItem(key)));

    this.downloadsInProgress.delete(bibleId);
    this.failedDownloads.delete(bibleId);
    this.staleStorageKeysCache = true;

    // don't track downloads deletion if caused by user canceling download
    if (!this.abortingDownloads.has(bibleId)) {
      trackBibleTextDownloadRemoval(bibleId);
    }

    this.deletingDownloads.delete(bibleId);
  }

  async waitForActiveDeletions(bibleId: string) {
    if (!this.deletingDownloads.has(bibleId)) return;

    return new Promise(resolve => {
      const poll = setInterval(() => {
        if (!this.deletingDownloads.has(bibleId)) {
          clearInterval(poll);
          resolve();
        }
      }, 100);
    });
  }

  async getDownloadProgress(bibleId: string) {
    const progress = new Progress();

    // bible
    const bibleRes = await this.store.getItem(getBibleUrl(bibleId));
    if (!bibleRes) return undefined;
    progress.bump(1);

    // books
    const booksRes = await this.store.getItem<{ data: Book[] }>(
      getBooksUrl(bibleId)
    );
    if (!booksRes) return undefined;
    progress.bump(1);

    // chapters
    const chapterIds = getChapterIds(booksRes.data);
    const chapterUrlKeys = chapterIds.map(id => getChapterUrl(bibleId, id));

    // add total and stored keys
    const totalChapterIds = chapterIds.length;
    const bibleStorageKeys = await this.getBibleStorageKeys(bibleId);
    const totalChaptersNotStored = difference(chapterUrlKeys, bibleStorageKeys)
      .length;

    progress.bump(
      totalChapterIds - totalChaptersNotStored,
      totalChapterIds + 2 // 2 === 1 bible getItem and 1 books getItem
    );

    return progress.current();
  }

  getDownloadState(bibleId: string, progress: number | undefined) {
    const midProgress = Boolean(progress && progress >= 0 && progress < 100);
    const isDownloading = this.downloadsInProgress.has(bibleId);
    const isFailed = this.failedDownloads.has(bibleId);
    const isAborting = this.abortingDownloads.has(bibleId);

    if (progress === 100) return "DOWNLOADED";
    if (isFailed) return "FAILED";
    if (isAborting) return "ABORTING";
    if (isDownloading && progress === undefined) return "INITIALIZING";
    if (isDownloading && midProgress) return "DOWNLOADING";
    if (midProgress) return "PARTIAL_DOWNLOAD";
    return "NOT_DOWNLOADED";
  }
}

export const bibleDownloader = new BibleDownloader(bibleStore);
